import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, Button, Skeleton, Typography } from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DriveFileMoveOutlinedIcon from "@mui/icons-material/DriveFileMoveOutlined";
import { CustomInput } from "global/components";
import SettingsLayout from "../components/SettingsLayout";
import { useTitle } from "utils/UseTitle";
import strings from "global/constants/StringConstants";
import { hasAccessTo } from "layouts/Config";
import { useAppDispatch } from "redux/hooks";
import { setActiveWorkspace } from "redux/authSlice";
import { store } from "utils/store";
import { handleErrorMessage, openSuccessNotification } from "helpers/methods";
import history from "utils/history";
import viewpaths from "global/constants/ViewPathConstants";
import { MAX_WORKSPACE_DEPTH, Workspace } from "types/workspace";
import {
  archiveWorkspace,
  fetchWorkspaces,
  renameWorkspace,
  unarchiveWorkspace,
} from "./WorkspacesServices";
import WorkspaceQuotaSection, { QuotaSkeleton } from "./WorkspaceQuotaSection";
import WorkspacesBackLink from "./components/WorkspacesBackLink";
import {
  AddSubWorkspaceDialog,
  MoveWorkspaceDialog,
} from "./components/WorkspaceTreeDialogs";
import { pathOf } from "./workspaceTree";
import { notifyWorkspacesChanged } from "./workspaceEvents";
import workspacesTokens from "./Workspaces.tokens";
import {
  buttonRowSx,
  childListSx,
  dangerDescriptionSx,
  dangerTitleSx,
  formCardSx,
  formFieldsSx,
  nameButtonSx,
  nameCellSx,
  pillSx,
  sectionDescriptionSx,
  sectionDividerSx,
  sectionHeaderSx,
  sectionHeaderTextSx,
  sectionTitleSx,
} from "./Workspaces.styles";

const { pill } = workspacesTokens;

const WorkspaceDetails = () => {
  useTitle("Workspace details");
  const { id: workspaceId } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  // Account admins: quotas and moving. Everything else follows the workspace's own flags, which
  // also cover a delegated admin (Workspace Admin of this workspace or one above it).
  const isAdmin = hasAccessTo(strings.resources.app, strings.adminPermission);

  const [ws, setWs] = useState<Workspace | null>(null);
  // Every workspace the caller manages: for the breadcrumb, the sub-workspace list and the move picker.
  const [all, setAll] = useState<Workspace[]>([]);
  const [nameInput, setNameInput] = useState<string>("");
  // true from the start, so "Workspace not found." never flashes before the first load
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [addUnder, setAddUnder] = useState<Workspace | null>(null);
  const [moving, setMoving] = useState<Workspace | null>(null);

  const load = async () => {
    if (!workspaceId) return;
    try {
      setLoading(true);
      const data = await fetchWorkspaces(true);
      const list = Array.isArray(data) ? data : [];
      const found = list.find((w) => w.id === workspaceId) ?? null;
      setAll(list);
      setWs(found);
      setNameInput(found?.name ?? "");
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Scope the active-workspace switch to this page's visit only — restore
    // whatever was active before on cleanup, so viewing a workspace's details
    // doesn't silently and permanently switch the user's active workspace
    // (which drives X-Workspace-Id and every hasWorkspacePermission check).
    const previousActiveWorkspaceId = store.getState().auth.activeWorkspaceId;
    if (workspaceId) {
      dispatch(setActiveWorkspace(workspaceId));
    }
    load();
    return () => {
      dispatch(setActiveWorkspace(previousActiveWorkspaceId));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  const saveName = async () => {
    if (!ws || !nameInput.trim()) return;
    try {
      setSubmitting(true);
      await renameWorkspace(ws.id, nameInput.trim());
      openSuccessNotification("Workspace renamed.");
      notifyWorkspacesChanged();
      await load();
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setSubmitting(false);
    }
  };

  const toggleArchive = async () => {
    if (!ws) return;
    try {
      setSubmitting(true);
      if (ws.archived) {
        await unarchiveWorkspace(ws.id);
        openSuccessNotification("Workspace restored.");
      } else {
        await archiveWorkspace(ws.id);
        openSuccessNotification("Workspace archived.");
      }
      notifyWorkspacesChanged();
      await load();
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setSubmitting(false);
    }
  };

  const renderSkeleton = () => (
    <Box sx={formCardSx} aria-hidden="true">
      <Skeleton variant="text" width="30%" sx={{ fontSize: "15px" }} />
      <Box sx={formFieldsSx}>
        <Skeleton variant="rounded" height={44} />
      </Box>
      <Skeleton variant="rounded" width={120} height={40} />
      {/* "Parent and sub-workspaces" block, so the quota block doesn't jump down when it lands. */}
      <Box sx={sectionDividerSx}>
        <Skeleton variant="text" width={200} sx={{ fontSize: "15px", mb: "4px" }} />
        <Skeleton variant="text" width="70%" height={18} sx={{ mb: "12px" }} />
        <Skeleton variant="rounded" width={150} height={40} />
      </Box>
      <QuotaSkeleton />
    </Box>
  );

  const canManage = (w: Workspace) => isAdmin || Boolean(w.canManage);

  /** Where the workspace sits, its sub-workspaces, and the add / move actions. */
  const renderTree = (workspace: Workspace) => {
    const parents = pathOf(all, workspace.id).slice(0, -1);
    const kids = all.filter((w) => w.parentId === workspace.id && w.id !== workspace.id);
    const activeKids = kids.filter((w) => !w.archived);
    const atMaxDepth = (workspace.depth ?? parents.length + 1) >= MAX_WORKSPACE_DEPTH;
    const hasActions = Boolean(workspace.canAddChild || workspace.canMove);
    return (
      <Box sx={sectionDividerSx}>
        {/* Same block shape as "Envelope quota" and "Archive workspace": h3, one description, then the list and buttons. */}
        <Typography component="h3" sx={dangerTitleSx}>
          Parent and sub-workspaces
        </Typography>
        <Typography
          component="div"
          sx={{
            ...(dangerDescriptionSx as object),
            ...(!hasActions && kids.length === 0 ? { mb: 0 } : {}),
          }}
        >
          {parents.length > 0
            ? `Sits under ${parents.map((p) => p.name).join(" › ")}.`
            : workspace.parentId
              ? "Sits under a workspace you don't manage."
              : "A top-level workspace."}{" "}
          {kids.length === 0
            ? "It has no sub-workspaces yet."
            : `It has ${activeKids.length} active sub-workspace${activeKids.length === 1 ? "" : "s"}.`}
          {atMaxDepth && !workspace.archived
            ? ` Workspaces can be nested up to ${MAX_WORKSPACE_DEPTH} levels deep, so this one can't have sub-workspaces.`
            : ""}
        </Typography>
        {kids.length > 0 && (
          <Box component="ul" sx={childListSx}>
            {kids.map((k) => (
              <Box component="li" key={k.id} sx={nameCellSx}>
                <Box
                  component="button"
                  type="button"
                  onClick={() => history.push(`${viewpaths.workspacesViewPath}/${k.id}/details`)}
                  sx={nameButtonSx}
                >
                  {k.name}
                </Box>
                {k.archived && (
                  <Box component="span" sx={pillSx(pill.warning)}>
                    Archived
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        )}
        {hasActions && (
          <Box sx={buttonRowSx}>
            {workspace.canAddChild && (
              <Button
                variant="outlined"
                startIcon={<AddRoundedIcon />}
                onClick={() => setAddUnder(workspace)}
                disabled={submitting}
              >
                Add sub-workspace
              </Button>
            )}
            {workspace.canMove && (
              <Button
                variant="outlined"
                startIcon={<DriveFileMoveOutlinedIcon />}
                onClick={() => setMoving(workspace)}
                disabled={submitting}
              >
                Move workspace
              </Button>
            )}
          </Box>
        )}
      </Box>
    );
  };

  const renderCard = (workspace: Workspace) => (
    <Box sx={formCardSx}>
      <Box sx={sectionHeaderSx}>
        <Box sx={sectionHeaderTextSx}>
          <Box sx={nameCellSx}>
            <Typography component="h2" sx={sectionTitleSx}>
              Workspace name
            </Typography>
            <Box
              component="span"
              sx={pillSx(workspace.archived ? pill.warning : pill.brand)}
            >
              {workspace.archived ? "Archived" : "Active"}
            </Box>
            {workspace.isDefault && (
              <Box component="span" sx={pillSx(pill.neutral)}>
                Default
              </Box>
            )}
          </Box>
          <Typography component="div" sx={sectionDescriptionSx}>
            Shown in the workspace switcher and on this workspace's pages.
          </Typography>
        </Box>
      </Box>

      <Box sx={formFieldsSx}>
        <CustomInput
          id="workspaceName"
          label="Name"
          placeHolder="Workspace name"
          name="workspaceName"
          value={nameInput}
          onChange={(e: any) => setNameInput(e.target.value)}
          disabled={!canManage(workspace) || workspace.archived}
        />
      </Box>

      {canManage(workspace) && !workspace.archived && (
        <Box sx={buttonRowSx}>
          <Button
            variant="contained"
            onClick={saveName}
            disabled={
              submitting ||
              !nameInput.trim() ||
              nameInput.trim() === workspace.name
            }
          >
            Save name
          </Button>
        </Box>
      )}

      {renderTree(workspace)}

      <WorkspaceQuotaSection
        workspaceId={workspace.id}
        isAdmin={isAdmin}
        archived={workspace.archived}
      />

      {(workspace.canArchive || (isAdmin && workspace.isDefault)) && renderArchive(workspace)}
    </Box>
  );

  const renderArchive = (workspace: Workspace) => {
    const activeKids = all.filter(
      (w) => w.parentId === workspace.id && w.id !== workspace.id && !w.archived,
    ).length;
    const parent = workspace.parentId ? all.find((w) => w.id === workspace.parentId) : undefined;
    const parentArchived = Boolean(workspace.archived && parent?.archived);
    const blocked = workspace.archived ? parentArchived : workspace.isDefault || activeKids > 0;
    return (
      <Box sx={sectionDividerSx}>
        <Typography component="h3" sx={dangerTitleSx}>
          {workspace.archived ? "Restore workspace" : "Archive workspace"}
        </Typography>
        <Typography component="div" sx={dangerDescriptionSx}>
          {workspace.isDefault
            ? "The Default workspace can't be archived."
            : workspace.archived
              ? parentArchived
                ? `"${parent?.name}" is archived. Restore it first.`
                : "Bring this workspace back to the active list."
              : activeKids > 0
                ? `It has ${activeKids} active sub-workspace${activeKids === 1 ? "" : "s"}. Archive or move ${activeKids === 1 ? "it" : "them"} first.`
                : "Hide this workspace from the list. You can restore it later."}
        </Typography>
        <Button
          variant="outlined"
          color={workspace.archived ? "primary" : "error"}
          onClick={toggleArchive}
          disabled={submitting || blocked}
        >
          {workspace.archived ? "Restore" : "Archive"}
        </Button>
      </Box>
    );
  };

  return (
    <SettingsLayout
      activeTab="Workspaces"
      title={ws ? `${ws.name} — Details` : "Workspace details"}
    >
      <WorkspacesBackLink />

      {/* Skeleton on the first load only: a reload after rename or archive keeps the page in place. */}
      {loading && !ws ? (
        renderSkeleton()
      ) : !ws ? (
        <Box sx={formCardSx}>
          <Typography sx={sectionDescriptionSx}>
            This workspace couldn't be found. It may have been removed.
          </Typography>
        </Box>
      ) : (
        renderCard(ws)
      )}

      <AddSubWorkspaceDialog parent={addUnder} onClose={() => setAddUnder(null)} onCreated={load} />
      <MoveWorkspaceDialog workspace={moving} all={all} onClose={() => setMoving(null)} onMoved={load} />
    </SettingsLayout>
  );
};

export default WorkspaceDetails;
